import { computed, defineComponent } from 'vue'
import type { GridColumnProps, GridRowProps } from './types'
import { useWindowSize } from '@vueuse/core'

const GridRow = defineComponent<GridRowProps>({
  setup(propz, ctx) {
    const wsize = propz.windowSize || useWindowSize()

    const props = computed(() => {
      if (propz.breakpoint && propz.breakpoint.width && wsize.width.value <= propz.breakpoint.width)
        return { ...propz, ...(propz.breakpoint.props || {}) }
      return propz
    })

    const columnsKey = computed(() =>
      props.value.implicit ? 'gridAutoColumns' : 'gridTemplateColumns'
    )
    const rowsKey = computed(() => (props.value.implicit ? 'gridAutoRows' : 'gridTemplateRows'))

    return () => {
      return (
        <div
          style={{
            display: 'grid',
            ...(props.value.breakpoint &&
              props.value.breakpoint.changeDirection &&
              props.value.breakpoint.width &&
              wsize.width.value <= props.value.breakpoint.width
              ? {
                [rowsKey.value]: props.value.layout || `repeat(auto-fit, minmax(1rem, 1fr))`,
                justifyContent: 'start'
              }
              : {
                [columnsKey.value]: props.value.layout || `repeat(auto-fit, minmax(1rem, 1fr))`,
                ...(props.value.startRight
                  ? {
                    justifyContent: 'end'
                  }
                  : {}),
                ...((props.value.style as any) || {})
              }),

            gap: props.value.gap,
            ...(props.value.justifyItems ? { justifyItems: props.value.justifyItems } : {}),
            ...(props.value.alignItems ? { alignItems: props.value.alignItems } : {}),
            ...(props.value.justifyContent ? { justifyContent: props.value.justifyContent } : {}),
            ...(props.value.alignContent ? { alignContent: props.value.alignContent } : {}),
            ...(props.value.areas ? { gridTemplateAreas: props.value.areas } : {})
          }}
        >
          {ctx.slots.default ? ctx.slots.default() : ''}
        </div>
      )
    }
  }
})

GridRow.props = [
  'layout',
  'gap',
  'startRight',
  'breakpoint',
  'implicit',
  'style',
  'windowSize',
  'justifyItems',
  'alignItems',
  'justifyContent',
  'alignContent',
  'areas'
]

const GridColumn = defineComponent<GridColumnProps>({
  setup(props, ctx) {
    const rowsKey = computed(() => (props.implicit ? 'gridAutoRows' : 'gridTemplateRows'))

    return () => {
      return (
        <div
          style={{
            display: 'grid',
            [rowsKey.value]: props.layout || `repeat(auto-fit, minmax(1rem, 1fr))`,
            gap: props.gap,
            ...(props.style || {}),
            ...(props.startRight
              ? {
                justifyContent: 'end'
              }
              : {}),
            ...(props.justifyItems ? { justifyItems: props.justifyItems } : {}),
            ...(props.alignItems ? { alignItems: props.alignItems } : {}),
            ...(props.justifyContent ? { justifyContent: props.justifyContent } : {}),
            ...(props.alignContent ? { alignContent: props.alignContent } : {}),
            ...(props.areas ? { gridTemplateAreas: props.areas } : {})
          }}
        >
          {ctx.slots.default ? ctx.slots.default() : ''}
        </div>
      )
    }
  }
})

GridColumn.props = [
  'layout',
  'gap',
  'startRight',
  'implicit',
  'justifyItems',
  'alignItems',
  'justifyContent',
  'alignContent',
  'areas'
]

const Grid = defineComponent<GridRowProps>({
  setup(propz, ctx) {
    const wsize = propz.windowSize || useWindowSize()

    const props = computed(() => {
      if (propz.breakpoint && propz.breakpoint.width && wsize.width.value <= propz.breakpoint.width)
        return { ...propz, ...(propz.breakpoint.props || {}) }
      return propz
    })

    const columnsKey = computed(() =>
      props.value.implicit ? 'gridAutoColumns' : 'gridTemplateColumns'
    )
    const rowsKey = computed(() => (props.value.implicit ? 'gridAutoRows' : 'gridTemplateRows'))

    const defaultLayout = computed(() =>
      propz.rows || propz.columns || propz.noDefaultLayout
        ? ''
        : `repeat(auto-fit, minmax(1rem, 1fr))`
    )

    return () => {
      return (
        <div
          style={{
            display: 'grid',
            ...(props.value.breakpoint &&
              props.value.breakpoint.changeDirection &&
              props.value.breakpoint.width &&
              wsize.width.value <= props.value.breakpoint.width
              ? {
                [rowsKey.value]: props.value.layout || defaultLayout.value,
                justifyContent: 'start'
              }
              : {
                [columnsKey.value]: props.value.layout || defaultLayout.value,
                ...(props.value.startRight
                  ? {
                    justifyContent: 'end'
                  }
                  : {}),
                ...((props.value.style as any) || {})
              }),

            direction: props.value.direction,
            gap: props.value.gap,
            ...(props.value.justifyItems ? { justifyItems: props.value.justifyItems } : {}),
            ...(props.value.alignItems ? { alignItems: props.value.alignItems } : {}),
            ...(props.value.justifyContent ? { justifyContent: props.value.justifyContent } : {}),
            ...(props.value.alignContent ? { alignContent: props.value.alignContent } : {}),
            ...(props.value.areas ? { gridTemplateAreas: props.value.areas } : {}),
            ...(props.value.columns ? { gridTemplateColumns: props.value.columns } : {}),
            ...(props.value.autoColumns ? { gridAutoColumns: props.value.autoColumns } : {}),
            ...(props.value.rows ? { gridTemplateRows: props.value.rows } : {}),
            ...(props.value.autoRows ? { gridAutoRows: props.value.autoRows } : {}),
            ...(props.value.autoFlow ? { gridAutoFlow: props.value.autoFlow } : {}),
            ...(propz.areas ? { gridTemplateAreas: propz.areas } : {})
          }}
        >
          {ctx.slots.default ? ctx.slots.default() : ''}
        </div>
      )
    }
  }
})

Grid.props = [
  'layout',
  'gap',
  'startRight',
  'breakpoint',
  'implicit',
  'style',
  'windowSize',
  'justifyItems',
  'alignItems',
  'justifyContent',
  'alignContent',
  'areas',
  'rows',
  'columns',
  'autoRows',
  'autoColumns',
  'autoFlow',
  'noDefaultLayout',
  'direction'
]
export { GridRow, GridColumn, Grid }
